import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface SmsTemplate {
  id?: number;
  group_id: number;
  template_type: string;
  name?: string;
  template: string;
  is_active: boolean;
  is_default: boolean;
  is_custom?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SmsTemplateResponse {
  success: boolean;
  data: SmsTemplate | SmsTemplate[];
  message?: string;
}

export interface PlaceholderResponse {
  success: boolean;
  data: Record<string, string>;
}

// Get all SMS templates
export const useSmsTemplates = () => {
  return useQuery({
    queryKey: ['sms-templates'],
    queryFn: async (): Promise<SmsTemplateResponse> => {
      const response = await api.get('/sms-templates');
      return response.data;
    },
  });
};

// Get a specific template by type
export const useSmsTemplate = (templateType: string) => {
  return useQuery({
    queryKey: ['sms-templates', templateType],
    queryFn: async (): Promise<SmsTemplateResponse> => {
      const response = await api.get(`/sms-templates/${templateType}`);
      return response.data;
    },
    enabled: !!templateType,
  });
};

// Get placeholders for a template type
export const useSmsTemplatePlaceholders = (templateType: string) => {
  return useQuery({
    queryKey: ['sms-templates', templateType, 'placeholders'],
    queryFn: async (): Promise<PlaceholderResponse> => {
      const response = await api.get(`/sms-templates/${templateType}/placeholders`);
      return response.data;
    },
    enabled: !!templateType,
  });
};

// Create or update SMS template
export const useSaveSmsTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { template_type: string; template: string; name?: string }): Promise<SmsTemplateResponse> => {
      const response = await api.post('/sms-templates', data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sms-templates'] });
      queryClient.invalidateQueries({ queryKey: ['sms-templates', variables.template_type] });
    },
  });
};

// Delete SMS template
export const useDeleteSmsTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (templateType: string): Promise<SmsTemplateResponse> => {
      const response = await api.delete(`/sms-templates/${templateType}`);
      return response.data;
    },
    onSuccess: (data, templateType) => {
      queryClient.invalidateQueries({ queryKey: ['sms-templates'] });
      queryClient.invalidateQueries({ queryKey: ['sms-templates', templateType] });
    },
  });
};


